// src/encounter/inserted-templates/use-inserted-templates.ts
// The controller of the templates a clinician adds from the "/" menu. Each one is an answer set of its own beside the
// note's primary set (QuestionBank answer sets): its own QuestionnaireResponse row, its own answers, its own autosave.
// The note's own save (`encounter-shell-page.tsx`) is not touched by any of this.
//
// Per set this holds the local state the block needs: the row id and version the next write is conditional on (adopted
// from every response, because a save after a sign may land on a NEW row), the answers (read from the stored row once,
// then the clinician's), a dirty generation, and a status. Saves are debounced like the note's (`AUTOSAVE_DEBOUNCE_MS`),
// go one at a time per set, and a page hide or unmount sends what is still pending with keepalive.
//
// A 409 `version-conflict` (or `not-current-revision`) keeps the clinician's answers: the set's head is read again, its
// id and version adopted, and the status says `conflict` until "Save again" puts the answers on the new version.
// A 409 `answer-set-removed` drops the set here and refreshes the list.
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import type { QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';
import type { FhirQuestionnaire } from '@/types/template-studio-api';
import type { QuestionnaireResponseResource, QRItemWire } from '@/types/questionnaire-response-save-api';
import { ANSWER_SET_SYSTEM, answerSetIdOf, readConflictCode } from '../lib/answer-set';
import { fromQuestionnaireResponseResource } from '../lib/from-questionnaire-response-resource';
import { partitionSavedResponse, preservedItems } from '../lib/orphaned-answers';
import { buildQrItemMeta, type QrItemMetaMap } from '../lib/qr-item-meta';
import type { InsertableTemplate } from '../structured-note/note-phrases';
import { isSupersededSave } from '../lib/superseded-save';
import { toQuestionnaireResponseResource } from '../lib/to-questionnaire-response-resource';
import { answerSetTemplateOptions } from '../services/use-answer-set-template';
import { encounterAnswerSetsKey, fetchAnswerSetHeads, useEncounterAnswerSets } from '../services/use-encounter-answer-sets';
import { useInsertedAnswerSetMutations, type AnswerSetRef } from '../services/use-inserted-answer-set-mutations';

/** The same quiet period the note's own autosave waits. */
export const AUTOSAVE_DEBOUNCE_MS = 1800;

export type { InsertableTemplate };

export type InsertedSetStatus = 'idle' | 'saving' | 'saved' | 'conflict' | 'error';

export interface InsertedSetView {
  answerSetId: string;
  rowId: string;
  title: string;
  /** The template the set was answered against; null while it loads or when it cannot be read. */
  questionnaire: FhirQuestionnaire | null;
  templateState: 'loading' | 'ready' | 'error';
  /** The answers; null until the template has loaded (they are read against its items). */
  response: QuestionnaireResponse | null;
  status: InsertedSetStatus;
}

export interface InsertedTemplatesConfig {
  encounterId: string;
  patientId: string;
  /** The encounter templates the page read (all of them, not only the picked one). */
  templates: readonly FhirQuestionnaire[];
  /** The template the note itself is on: never offered for insertion. */
  mainTemplateId: string | null;
}

export interface InsertedTemplates {
  sets: InsertedSetView[];
  insertable: InsertableTemplate[];
  /** An insert is in flight, or the last one failed (a short line says so). */
  inserting: boolean;
  insertFailed: boolean;
  insert: (questionnaireId: string) => void;
  change: (answerSetId: string, response: QuestionnaireResponse) => void;
  retry: (answerSetId: string) => void;
  remove: (answerSetId: string) => void;
}

interface Local {
  rowId: string;
  versionId: string;
  /** The row the template was first read for (the template read stays keyed to it when a save moves the set to a new row). */
  templateRowId: string;
  questionnaireId: string;
  response: QuestionnaireResponse | null;
  orphans: QRItemWire[];
  generation: number;
  savedGeneration: number;
  /** The generation the save in flight carries; null when none is. */
  inFlight: number | null;
  status: InsertedSetStatus;
}

const metaCache = new WeakMap<FhirQuestionnaire, QrItemMetaMap>();
function metaOf(template: FhirQuestionnaire): QrItemMetaMap {
  let meta = metaCache.get(template);
  if (!meta) {
    meta = buildQrItemMeta(template.item);
    metaCache.set(template, meta);
  }
  return meta;
}

/** `Questionnaire/{id}` or `{id}` or `{id}|{version}` → the id. */
export function questionnaireIdOf(ref: string | null | undefined): string {
  return (ref ?? '').replace(/^Questionnaire\//, '').split('|')[0];
}

/** The templates a clinician may add: the active ones, except the one the note is on. */
export function insertableTemplates(templates: readonly FhirQuestionnaire[], mainTemplateId: string | null): InsertableTemplate[] {
  return templates
    .filter((t) => !!t.id && t.status === 'active' && t.id !== mainTemplateId)
    .map((t) => ({ id: t.id as string, title: t.title?.trim() || 'Untitled template' }));
}

const answersKey = (r: QuestionnaireResponse) => JSON.stringify(r.answers);

export function useInsertedTemplates(config: InsertedTemplatesConfig): InsertedTemplates {
  const { encounterId, patientId, templates, mainTemplateId } = config;
  const qc = useQueryClient();
  const headsQuery = useEncounterAnswerSets(encounterId);
  const mutations = useInsertedAnswerSetMutations(encounterId);
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const locals = useRef(new Map<string, Local>());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const [insertState, setInsertState] = useState({ pending: 0, failed: false });

  const heads = headsQuery.data ?? NO_HEADS;
  const live = new Set<string>();
  for (const head of heads) {
    const id = answerSetIdOf(head);
    if (!id || !head.id) continue;
    live.add(id);
    if (!locals.current.has(id)) {
      locals.current.set(id, {
        rowId: head.id,
        versionId: head.meta?.versionId ?? '',
        templateRowId: head.id,
        questionnaireId: questionnaireIdOf(head.questionnaire),
        response: null,
        orphans: [],
        generation: 0,
        savedGeneration: 0,
        inFlight: null,
        status: 'idle',
      });
    }
  }
  // A set the list no longer has (removed here or elsewhere) leaves with it.
  for (const id of [...locals.current.keys()]) if (!live.has(id)) locals.current.delete(id);

  // The template of each set: the one in the encounter-templates list when it is there, else the row's own template.
  const byId = new Map(templates.map((t) => [t.id ?? '', t]));
  const fromList = (id: string): FhirQuestionnaire | undefined => byId.get(locals.current.get(id)?.questionnaireId ?? '');
  const needRead = heads.map((h) => answerSetIdOf(h)).filter((id): id is string => !!id && !fromList(id));
  const reads = useQueries({
    queries: needRead.map((id) => answerSetTemplateOptions(locals.current.get(id)?.templateRowId ?? null)),
  });
  const readOf = new Map(needRead.map((id, i) => [id, reads[i]]));

  const sets: InsertedSetView[] = [];
  for (const head of heads) {
    const id = answerSetIdOf(head);
    const local = id ? locals.current.get(id) : undefined;
    if (!id || !local) continue;
    const listed = fromList(id);
    const read = readOf.get(id);
    const template = listed ?? read?.data ?? null;
    const templateState: InsertedSetView['templateState'] = template ? 'ready' : read?.isError || read?.data === null ? 'error' : 'loading';
    // The stored answers are read once, against the template's items; from then on the clinician's answers are the set's.
    if (template && !local.response) {
      const { hydratable, orphans } = partitionSavedResponse(head, metaOf(template), false);
      local.response = fromQuestionnaireResponseResource(hydratable, metaOf(template));
      local.orphans = preservedItems(orphans);
    }
    sets.push({
      answerSetId: id,
      rowId: local.rowId,
      title: template?.title?.trim() || 'Template',
      questionnaire: template,
      templateState,
      response: local.response,
      status: local.status,
    });
  }

  const templateOf = useRef<(id: string) => FhirQuestionnaire | null>(() => null);
  templateOf.current = (id) => fromList(id) ?? readOf.get(id)?.data ?? null;

  const body = (id: string, response: QuestionnaireResponse): QuestionnaireResponseResource | null => {
    const local = locals.current.get(id);
    const template = templateOf.current(id);
    if (!local || !template) return null;
    const resource = toQuestionnaireResponseResource(
      response,
      { questionnaireId: local.questionnaireId || (template.id ?? ''), patientId, encounterId, id: local.rowId, ...(local.orphans.length ? { preserved: local.orphans } : {}) },
      metaOf(template),
    );
    return { ...resource, identifier: { system: ANSWER_SET_SYSTEM, value: id } };
  };
  const refOf = (id: string, local: Local): AnswerSetRef => ({ answerSetId: id, rowId: local.rowId, versionId: local.versionId });

  /** Read the set's head again after a conflict and adopt its row and version (the answers stay the clinician's). */
  const adoptHead = useCallback(
    async (id: string, currentVersion?: string) => {
      const local = locals.current.get(id);
      if (!local) return;
      try {
        const fresh = await fetchAnswerSetHeads(encounterId);
        qc.setQueryData(encounterAnswerSetsKey(encounterId), fresh);
        const head = fresh.find((h) => answerSetIdOf(h) === id);
        if (!head?.id) {
          locals.current.delete(id); // gone on the server: removed elsewhere
          return;
        }
        local.rowId = head.id;
        local.versionId = head.meta?.versionId ?? currentVersion ?? local.versionId;
      } catch {
        if (currentVersion) local.versionId = currentVersion;
      }
    },
    [encounterId, qc],
  );

  const saveNow = useCallback(
    async (id: string, opts: { keepalive?: boolean } = {}): Promise<void> => {
      const timer = timers.current.get(id);
      if (timer !== undefined) clearTimeout(timer);
      timers.current.delete(id);
      const local = locals.current.get(id);
      if (!local?.response || local.generation === local.savedGeneration) return;
      // One save at a time per set: an edit made during it goes next, from its success. Only a page hide goes at once
      // (the save in flight is cancelled by the newer one), and only when it carries something newer.
      if (local.inFlight !== null && (!opts.keepalive || local.inFlight >= local.generation)) return;
      const resource = body(id, local.response);
      if (!resource) return;
      const sent = local.generation;
      local.inFlight = sent;
      local.status = 'saving';
      rerender();
      try {
        const stored = await mutations.save(refOf(id, local), resource, opts);
        local.rowId = stored.id ?? local.rowId;
        local.versionId = stored.meta?.versionId ?? local.versionId;
        local.inFlight = null;
        local.savedGeneration = Math.max(local.savedGeneration, sent);
        if (local.generation > local.savedGeneration) {
          local.status = 'idle';
          schedule(id);
        } else {
          local.status = 'saved';
        }
      } catch (e) {
        if (isSupersededSave(e)) return; // a newer save of this set carries on
        local.inFlight = null;
        const conflict = readConflictCode(e);
        if (conflict?.code === 'answer-set-removed') {
          locals.current.delete(id);
          void qc.invalidateQueries({ queryKey: encounterAnswerSetsKey(encounterId) });
        } else if (conflict?.code === 'version-conflict' || conflict?.code === 'not-current-revision') {
          await adoptHead(id, conflict.currentVersion);
          if (locals.current.has(id)) local.status = 'conflict';
        } else {
          local.status = 'error';
        }
      }
      rerender();
    },
    // `body` and `schedule` read refs and stable values only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutations, qc, encounterId, adoptHead],
  );

  const saveNowRef = useRef(saveNow);
  saveNowRef.current = saveNow;
  function schedule(id: string) {
    const prev = timers.current.get(id);
    if (prev !== undefined) clearTimeout(prev);
    timers.current.set(
      id,
      setTimeout(() => {
        timers.current.delete(id);
        void saveNowRef.current(id);
      }, AUTOSAVE_DEBOUNCE_MS),
    );
  }

  const change = useCallback((id: string, response: QuestionnaireResponse) => {
    const local = locals.current.get(id);
    if (!local) return;
    // The renderer may emit what it was given (a computed value, a re-mount): nothing changed, nothing to save.
    if (local.response && answersKey(local.response) === answersKey(response)) return;
    local.response = response;
    local.generation += 1;
    if (local.status !== 'conflict') {
      local.status = local.inFlight !== null ? 'saving' : 'idle';
      schedule(id);
    }
    rerender();
  }, []);

  const retry = useCallback(
    (id: string) => {
      const local = locals.current.get(id);
      if (!local) return;
      local.status = 'idle';
      void saveNowRef.current(id);
    },
    [],
  );

  const insert = useCallback(
    (questionnaireId: string) => {
      setInsertState((s) => ({ pending: s.pending + 1, failed: false }));
      const create = toQuestionnaireResponseResource(
        { resourceType: 'QuestionnaireResponse', status: 'in-progress', answers: [] },
        { questionnaireId, patientId, encounterId },
      );
      mutations.create(create).then(
        () => setInsertState((s) => ({ pending: s.pending - 1, failed: s.failed })),
        () => setInsertState((s) => ({ pending: s.pending - 1, failed: true })),
      );
    },
    [mutations, patientId, encounterId, setInsertState],
  );

  const remove = useCallback(
    async (id: string) => {
      const local = locals.current.get(id);
      if (!local) return;
      const timer = timers.current.get(id);
      if (timer !== undefined) clearTimeout(timer);
      timers.current.delete(id);
      // A set whose template cannot be read is removed as it is stored (its head), so "Remove" always works.
      const head = qc
        .getQueryData<QuestionnaireResponseResource[]>(encounterAnswerSetsKey(encounterId))
        ?.find((h) => answerSetIdOf(h) === id);
      const resource =
        body(id, local.response ?? { resourceType: 'QuestionnaireResponse', status: 'in-progress', answers: [] }) ??
        (head ? { ...head, id: local.rowId } : null);
      if (!resource) return;
      const attempt = () => mutations.remove(refOf(id, local), resource);
      try {
        try {
          await attempt();
        } catch (e) {
          const conflict = readConflictCode(e);
          if (conflict?.code !== 'version-conflict' && conflict?.code !== 'not-current-revision') throw e;
          await adoptHead(id, conflict.currentVersion);
          if (!locals.current.has(id)) return; // already gone
          await mutations.remove(refOf(id, local), { ...resource, id: local.rowId });
        }
        locals.current.delete(id);
      } catch (e) {
        if (readConflictCode(e)?.code === 'answer-set-removed') locals.current.delete(id);
        else local.status = 'error';
      }
      rerender();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutations, adoptHead, qc, encounterId],
  );

  // A page hide (a refresh, a closed tab, a hidden tab) and a leave send what is still pending, with keepalive on a hide.
  useEffect(() => {
    const pending = (keepalive: boolean) => {
      for (const [id, local] of locals.current) {
        if (local.generation > local.savedGeneration && local.status !== 'conflict') void saveNowRef.current(id, { keepalive });
      }
    };
    const onHide = () => {
      if (document.visibilityState !== 'visible') pending(true);
    };
    const onPageHide = () => pending(true);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    const timerMap = timers.current;
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
      pending(false);
      timerMap.forEach((t) => clearTimeout(t));
      timerMap.clear();
    };
  }, []);

  return {
    sets,
    insertable: insertableTemplates(templates, mainTemplateId),
    inserting: insertState.pending > 0,
    insertFailed: insertState.failed,
    insert,
    change,
    retry,
    remove: (id: string) => void remove(id),
  };
}

const NO_HEADS: QuestionnaireResponseResource[] = [];
